import Ajv from 'ajv';
import isPlainObject from 'lodash/isPlainObject';

import { hasDuplicateKeys, JSON_SCHEMA_TYPES } from '../schema-model';

export type ValidationError = {
  path: string;
  message: string;
};

type SchemaNode = Record<string, unknown>;

const isObject = (value: unknown): value is SchemaNode => isPlainObject(value);

const ajv = new Ajv({
  strict: false,
  validateSchema: true,
  allErrors: false,
});

const metaSchema = ajv.getSchema('http://json-schema.org/draft-07/schema');

const validateSchema = (schemaString: string): ValidationError[] => {
  const errors: ValidationError[] = [];

  // Skip validation for empty strings
  if (!schemaString || schemaString.trim() === '') {
    return errors;
  }

  // Check for duplicate keys before parsing
  if (hasDuplicateKeys(schemaString)) {
    errors.push({ path: '', message: 'Duplicate keys detected in JSON' });
    return errors;
  }

  let schema: unknown;
  try {
    schema = JSON.parse(schemaString);
  } catch (e) {
    errors.push({ path: '', message: `Invalid JSON: ${(e as Error).message}` });
    return errors;
  }

  if (!isObject(schema)) {
    errors.push({ path: '', message: 'Schema must be an object' });
    return errors;
  }

  if (metaSchema) {
    const valid = metaSchema(schema);
    if (!valid && metaSchema.errors) {
      const specificErrors = metaSchema.errors.filter(e => !['anyOf', 'oneOf', 'allOf'].includes(e.keyword));

      // Use specific errors if available, otherwise fall back to all errors
      // The reason is anyOf, oneOf, and allOf creates a lot of noise
      // The noise is confusing for the user and we don't support them
      const errorsToCheck = specificErrors.length > 0 ? specificErrors : metaSchema.errors;

      const priorityKeywords = ['enum', 'const', 'type', 'required', 'format'];
      const err = errorsToCheck.find(e => priorityKeywords.includes(e.keyword)) || errorsToCheck[0];

      if (err) {
        errors.push({
          path: err.instancePath || '',
          message: err.message || 'Invalid schema',
        });
      }
    }
  }

  validateConstraints(schema, errors);

  return errors;
};

/**
 * Validates structured output constraints
 * These are based on the constraints on the OpenAI structured output docs: https://platform.openai.com/docs/guides/structured-outputs#supported-schemas
 * The only thing we don't support is the anyOf keyword
 */
const validateConstraints = (schema: SchemaNode, errors: ValidationError[]): void => {
  // Root must be type: object
  if (schema.type !== 'object') {
    errors.push({ path: '', message: 'Root schema must be type "object"' });
  }

  // Collect $defs for ref validation
  const defs: SchemaNode = {
    ...(isObject(schema.$defs) ? schema.$defs : {}),
    ...(isObject(schema.definitions) ? schema.definitions : {}),
  };

  // Validate definitions
  for (const [name, defSchema] of Object.entries(defs)) {
    validateNode(defSchema, `$defs.${name}`, errors, defs);
  }

  // Validate root schema
  validateNode(schema, '', errors, defs);
};

const validateNode = (node: unknown, path: string, errors: ValidationError[], defs: SchemaNode): void => {
  if (!isObject(node)) return;

  // Check for unsupported keywords
  // If we want to support more or less, we can adjust
  const unsupportedKeywords = [
    'not',
    'if',
    'then',
    'else',
    'patternProperties',
    'propertyNames',
    'dependencies',
    'dependentRequired',
    'dependentSchemas',
  ];

  for (const keyword of unsupportedKeywords) {
    if (keyword in node) {
      errors.push({ path, message: `${keyword} is not supported` });
    }
  }

  // Handle $ref - just validate it points to existing def
  if ('$ref' in node) {
    const ref = node.$ref;
    if (typeof ref === 'string') {
      const match = ref.match(/^#\/(\$defs|definitions)\/(.+)$/);
      if (match && !(match[2] in defs)) {
        errors.push({ path, message: `$ref "${ref}" points to non-existent definition` });
      }
    }
    return;
  }

  const type = node.type;
  const hasComposite = 'anyOf' in node || 'oneOf' in node || 'allOf' in node;
  const hasEnum = 'enum' in node;

  // Check for missing or invalid type (unless it's a composite or enum)
  if (!hasComposite && !hasEnum && !JSON_SCHEMA_TYPES.includes(type as (typeof JSON_SCHEMA_TYPES)[number])) {
    errors.push({ path, message: 'Missing or invalid "type"' });
  }

  // Object-specific validations
  if (type === 'object') {
    // additionalProperties: true is not allowed (missing or false is ok)
    if (node.additionalProperties === true) {
      errors.push({ path, message: 'additionalProperties: true is not allowed' });
    }

    // All properties must be required
    if (isObject(node.properties)) {
      const propKeys = Object.keys(node.properties);
      const required = Array.isArray(node.required) ? node.required : [];
      const missing = propKeys.filter(k => !required.includes(k));

      if (missing.length > 0) {
        errors.push({ path, message: `All properties must be required. Missing: ${missing.join(', ')}` });
      }

      // Recurse into properties
      for (const [key, prop] of Object.entries(node.properties)) {
        validateNode(prop, path ? `${path}.properties.${key}` : `properties.${key}`, errors, defs);
      }
    }
  }

  // Array - recurse into items
  if (type === 'array' && isObject(node.items)) {
    validateNode(node.items, `${path}.items`, errors, defs);
  }

  // Composite types - recurse into schemas
  const compositeModes = ['anyOf', 'oneOf', 'allOf'] as const;
  for (const mode of compositeModes) {
    if (mode in node && Array.isArray(node[mode])) {
      (node[mode] as unknown[]).forEach((schema, index) => {
        if (isObject(schema)) {
          validateNode(schema, `${path}.${mode}[schema #${index + 1}]`, errors, defs);
        }
      });
    }
  }
};

export default validateSchema;
